import React, { useCallback } from 'react';
import {
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

import { Pill, PillTone } from '../Pill';

export interface PillSelectOption {
  label: React.ReactNode;
  tone?: PillTone;
  value: number | string;
}

export interface PillSelectProps
  extends Omit<SelectProps<number | string>, 'onChange' | 'renderValue'> {
  onChange: (event: SelectChangeEvent<number | string>) => void;
  options: PillSelectOption[];
}

const selectStyles = {
  minWidth: 148,
  height: 38,
  borderRadius: 1.5,
  bgcolor: 'transparent',
  transition: 'background-color 140ms ease',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
  '&:hover': { bgcolor: 'action.hover' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'softBorder' },
  '&.Mui-focused': { bgcolor: 'action.hover' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: 1,
    borderColor: 'outline',
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    py: 0.5,
    pl: 0.5,
    pr: '34px !important',
  },
  '& .MuiSelect-icon': {
    right: 8,
    opacity: 0,
    transition: 'opacity 140ms ease',
  },
  '&:hover .MuiSelect-icon, &.Mui-focused .MuiSelect-icon': { opacity: 1 },
};

const PillSelect = React.memo(
  ({ onChange, options, sx, value, ...props }: PillSelectProps) => {
    const renderValue = useCallback(
      (selected: number | string) => {
        const option = options.find((item) => item.value === selected);

        return (
          <Pill tone={option?.tone || 'neutral'}>
            {option?.label || selected}
          </Pill>
        );
      },
      [options]
    );
    const combinedStyles = (sx
      ? [selectStyles, sx]
      : selectStyles) as SxProps<Theme>;

    return (
      <Select
        {...props}
        value={value}
        onChange={onChange}
        renderValue={renderValue}
        size="small"
        sx={combinedStyles}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Pill tone={option.tone}>{option.label}</Pill>
          </MenuItem>
        ))}
      </Select>
    );
  }
);

export { PillSelect };
